import { Image as ImageIcon } from '@styled-icons/evil';
import Image from 'next/image';
import { ErrorOutline } from '@styled-icons/material';
import { ImageElement } from '@typedream/components';
import {
  DreamValue,
  ImageElement as ImageElementType,
} from '@typedream/core';
import { PlateRenderElementProps } from '@udecode/plate-core';
import { pick } from 'lodash';

import { DEFAULT_MAX_WIDTH } from '../../../constants';
import { useTPlateEditorRef } from '../../../plate';
import { DreamEditor } from '../../../types';
import { checkImageUrlFromNotion } from '../../../utils/checkImageUrlSource';

type EmptyImagePlaceholderProps = PlateRenderElementProps<
DreamValue,
ImageElementType
>;

function EmptyImagePlaceholder(props: EmptyImagePlaceholderProps) {
  const {
    editor: e, element, className, attributes, children,
  } = props;

  const editor = e as DreamEditor;

  return (
    <div
      style={{
        width: element.width || DEFAULT_MAX_WIDTH,
        maxWidth: '100%',
        minHeight: element.height,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
      }}
      className={className}
      {...attributes}
    >
      <div
        role="none"
        contentEditable={false}
        style={{
          height: 50,
          borderRadius: 10,
          color: 'var(--accent)',
          backgroundColor: 'var(--background-button)',
          border: '1px solid var(--border-modal)',
          boxShadow: 'var(--shadow-modal)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          margin: '10px 0',
          cursor: 'pointer',
          userSelect: 'none',
        }}
        onMouseDown={() => editor.emitter.emit('toolbar:toggle-replace-image')}
      >
        <ImageIcon size={28} style={{ marginRight: 5 }} />
        Add an image
      </div>

      {children}
    </div>
  );
}

export function ImageElementEditor(
  props: PlateRenderElementProps<DreamValue, ImageElementType & {
    collection_connect_data?: boolean;
    collection_connect_data_keys?: {
      schemaKey: string
      blockElementKey:
      | 'text'
      | 'url'
      | 'backgroundImage'
      | 'href'
      | 'html'
      | 'file'
    }[]
  }>,
) {
  const { element } = props;

  const editor = useTPlateEditorRef();
  const imageUrlFromNotion = checkImageUrlFromNotion(element?.url);

  return element.url ? (
    <>
      {imageUrlFromNotion && !element.collection_connect_data && (
        <div
          className="is-flex is-justify-content-center px-4 my-3"
          style={{
            display: 'flex',
            justifyContent: 'center',
            padding: '0 16px',
            margin: '0 12px',
          }}
          contentEditable={false}
        >
          <div
            role="button"
            tabIndex={0}
            onMouseDown={() => {
              editor.emitter.emit('popup:toggle-image-notion-alert');
            }}
            style={{
              display: 'flex',
              gap: 10,
              padding: '8px 8px 8px 12px',
              color: 'var(--error-400)',
              fontSize: 10,
              fontWeight: 500,
              background: 'var(--neutral-25)',
              boxShadow: '0px 4px 11px rgba(0, 0, 0, 0.1)',
              borderRadius: 6,
              marginBottom: 22,
              cursor: 'pointer',
            }}
          >
            <ErrorOutline size={16} />
            This image will expire in 1 hour. Click here to learn more -&gt;
          </div>
        </div>
      )}
      <ImageElement
        {...pick(props, ['element', 'className', 'children', 'attributes'])}
        optimizedImageComponent={Image as any}
      />
    </>
  ) : (
    <EmptyImagePlaceholder {...props} />
  );
}
